import Typography from '@mui/material/Typography'
import { connect, useSelector } from 'react-redux'
import { FormattedMessage } from 'react-intl'
import RegisterToTourButton from '../ui/RegisterToTourButton'
import { getFormattedDateTime } from '@app/utils/date'
import { Grid } from '@mui/material'
import { makeStyles } from '@mui/styles'
import { pageShouldShowHeroSignUpButton } from '@app/utils/pageUtils'
import { TabsEnum } from '@app/utils/enums'
import { signUpEnabled } from '@app/utils/signUpUtils'
import { DateTime } from 'luxon'
import SimpleTooltip from '../ui/SimpleTooltip'
import { RootState } from '@app/store'

const useStyles = makeStyles(() => ({
  signUpText: {
    marginBottom: 16,
  },
  loginText: {
    textAlign: 'center',
    maxWidth: 500,
  },
  entryDeadlineCancelText: {
    fontWeight: 'bold',
    textAlign: 'center',
    marginTop: '10px',
  },
}))

type Props = {
  site?: TournamentSite
  onlyBtn?: boolean
}

const SignUp = ({ site, onlyBtn = false }: Props) => {
  const classes = useStyles()
  const currentPage: TabsEnum | null = useSelector(
    (store: StoreState) => store.pageReducer.currentPage || null,
  ) as TabsEnum

  const courseTimezone = useSelector(
    (store: RootState) => store.tournamentReducer.tournamentSite.tournament.rounds[0].club.timeZone,
  )

  if (!site || !site.onlineEntriesEnabled) {
    return null
  }

  const registerBtnMargin = '20px 0 15px'

  if (onlyBtn) {
    const showButton = pageShouldShowHeroSignUpButton(currentPage) || signUpEnabled(site)
    if (!showButton) {
      return
    }
    return (
      <Grid container sx={{ width: 1 }} justifyContent="center" alignItems="center">
        <Grid item>
          <RegisterToTourButton style={{ margin: registerBtnMargin }} />
        </Grid>
      </Grid>
    )
  }

  const entryOpenDateTime = DateTime.fromISO(site.registrationStartsTime).setZone(courseTimezone)
  const entryEndDateTime = DateTime.fromISO(site.registrationEndsTime).setZone(courseTimezone)
  const cancelByDateTime = DateTime.fromISO(site.registrationCancellingEndsTime).setZone(courseTimezone)

  return (
    <Grid container direction="column" justifyContent="center" alignItems="center">
      <Grid item>
        <Typography variant="h2" className={classes.signUpText}>
          <FormattedMessage id={'buttons.signUp'} />
        </Typography>
      </Grid>
      <Grid item>
        <Typography variant="body1" className={classes.loginText}>
          <FormattedMessage id={'strings.loginText'} />
        </Typography>
      </Grid>
      <Grid item>
        <RegisterToTourButton style={{ margin: registerBtnMargin }} />
      </Grid>
      {site.registerAndCancelTimeFramesEnabled && (
        <Grid item>
          <Typography variant={'body1'} className={classes.entryDeadlineCancelText}>
            <FormattedMessage
              id="strings.entryDeadline"
              values={{
                startDate: (
                  <SimpleTooltip title={`${entryOpenDateTime.zoneName} (${entryOpenDateTime.offsetNameShort})`}>
                    <span>{getFormattedDateTime(entryOpenDateTime, site.units)}</span>
                  </SimpleTooltip>
                ),
                endDate: (
                  <SimpleTooltip title={`${entryEndDateTime.zoneName} (${entryEndDateTime.offsetNameShort})`}>
                    <span>{getFormattedDateTime(entryEndDateTime, site.units)}</span>
                  </SimpleTooltip>
                ),
              }}
            />
            <br />
            <FormattedMessage
              id="strings.cancellationsBy"
              values={{
                date: (
                  <SimpleTooltip title={`${cancelByDateTime.zoneName} (${cancelByDateTime.offsetNameShort})`}>
                    <span>{getFormattedDateTime(cancelByDateTime, site.units)}</span>
                  </SimpleTooltip>
                ),
              }}
            />
          </Typography>
        </Grid>
      )}
    </Grid>
  )
}

export default connect((state: StoreState) => ({
  site: state.tournamentReducer.tournamentSite,
}))(SignUp)
